import { Dialog, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useAdminData } from "@providers/AdminDataProvider";

interface EHROrgContactDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  // When the org credentials were rejected (vs never set up), show the
  // "credentials are no longer valid" messaging.
  invalidCredentials?: boolean;
}

/**
 * Shown to providers in organizations that use an org-level admin EHR account
 * (ehrIntegrationType === ORG_USERNAME_PASSWORD) when the org isn't connected.
 *
 * These providers have no credentials of their own and cannot complete the
 * per-provider login flow, so instead of the EHRLoginDialog we surface a
 * matching-format dialog directing them to their organization administrator.
 */
export const EHROrgContactDialog: React.FC<EHROrgContactDialogProps> = ({
  open,
  setOpen,
  invalidCredentials = false,
}) => {
  const { organizationLogo, externalOrganizationName } = useAdminData();

  return (
    <Dialog
      size="xl"
      open={open}
      setOpen={setOpen}
      title={invalidCredentials ? "EHR connection error" : "Connect your EHR"}
      showCloseButton
      primaryButton={{
        text: "Close",
        onClick: () => setOpen(false),
      }}
      secondaryButton={undefined}
    >
      <div className="flex flex-col items-center gap-4 py-4">
        {organizationLogo ? (
          <img className="w-20 h-20" src={organizationLogo} alt={`${externalOrganizationName} logo`} />
        ) : null}
        <Paragraph displayType="loud" colorType="primary" className="text-center">
          {invalidCredentials ? "Your EHR credentials are no longer valid" : "Your EHR isn't connected"}
        </Paragraph>
        <Paragraph colorType="secondary" className="text-center">
          {invalidCredentials
            ? "Your organization's EHR credentials are no longer valid. Please contact your organization administrator to restore the connection."
            : "Please contact your organization administrator to set up your EHR connection."}
        </Paragraph>
      </div>
    </Dialog>
  );
};
